import { Link } from "react-router-dom";
import { Trash2, ScrollText, Palette, FileText, Radar, Loader2 } from "lucide-react";
import { cn } from "@/lib/util";
import { usePermissions } from "@/lib/permissions";

/**
 * Action buttons + risk-score badge shown on the ClientDetail header.
 * Buttons are gated by the caller's role:
 *   - Delete + Marca:            admin only
 *   - Digest preview + Deep sweep: admin + soc_analyst (download-capable roles)
 *   - Compliance link:           anyone (read-only view)
 */
export function ClientHeaderActions({
  clientId, client, tone, digestBusy, deepBusy,
  onDelete, onBranding, onDigestPreview, onDeepSweep,
}) {
  const { can } = usePermissions();
  return (
    <div className="flex items-end gap-2 flex-wrap">
      {can("client.delete") && (
        <button
          onClick={onDelete}
          className="border border-sev-critical/60 text-sev-critical px-3 py-2 hover:bg-sev-critical/10 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2"
          data-testid="client-delete-btn"
          title="Dar de baja este cliente y purgar todos los datos asociados"
        >
          <Trash2 className="w-3 h-3" /> Eliminar
        </button>
      )}
      <Link
        to={`/clients/${clientId}/compliance`}
        className="border border-primary/60 text-primary px-3 py-2 hover:bg-primary/10 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2"
        data-testid="client-compliance-btn"
        title="Ver el registro de controles ISO 27001, NIST CSF y ENS mapeado desde los hallazgos"
      >
        <ScrollText className="w-3 h-3" /> Cumplimiento
      </Link>
      {can("client.branding") && (
        <button
          onClick={onBranding}
          className="border border-primary/60 text-primary px-3 py-2 hover:bg-primary/10 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2"
          data-testid="client-branding-btn"
        >
          <Palette className="w-3 h-3" /> Marca
        </button>
      )}
      {can("report.download") && (
        <button
          onClick={onDigestPreview} disabled={digestBusy}
          className="border border-primary/60 text-primary px-3 py-2 hover:bg-primary/10 disabled:opacity-40 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2"
          data-testid="digest-preview-btn"
          title="Previsualiza el PDF semanal que se enviaría este lunes"
        >
          {digestBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
          Vista previa resumen
        </button>
      )}
      {can("scan.run") && (
        <button
          onClick={onDeepSweep}
          disabled={deepBusy || !(client.ip_ranges && client.ip_ranges.length)}
          className="border border-sev-high/60 text-sev-high px-3 py-2 hover:bg-sev-high/10 disabled:opacity-40 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2"
          data-testid="deep-sweep-btn"
          title={client.ip_ranges?.length ? "Expande cada CIDR y consulta cada host en Shodan" : "No hay rangos IP registrados"}
        >
          {deepBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />}
          Barrido profundo
        </button>
      )}
      <div className="text-right">
        <div className="overline">Puntaje de riesgo</div>
        <div className={cn("font-display font-bold leading-none mt-1", tone.text)} style={{ fontSize: "72px" }}
             data-testid="client-detail-risk">{client.risk_score}</div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mt-1">/100 · {tone.label}</div>
      </div>
    </div>
  );
}
